import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ReviewModal from './components/ReviewModal';
import OrderTimeline from './components/OrderTimeline';

type TabType = 'compras' | 'subastas' | 'pujas';

const MisComprasPage = () => {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('compras');
  const [compras, setCompras] = useState<any[]>([]);
  const [subastasGanadas, setSubastasGanadas] = useState<any[]>([]);
  const [misPujas, setMisPujas] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [profileId, setProfileId] = useState<string>('');
  const [reviewModal, setReviewModal] = useState<any>(null);
  const [misReviews, setMisReviews] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!loading) {
      if (!user) { navigate('/login'); return; }
      fetchProfileAndData();
    }
  }, [user, loading]);

  const fetchProfileAndData = async () => {
    setLoadingData(true);
    const { data: perfil } = await supabase
      .from('profiles')
      .select('id')
      .eq('user_id', user!.id)
      .maybeSingle();

    if (!perfil) { setLoadingData(false); return; }
    setProfileId(perfil.id);
    await Promise.all([
      fetchCompras(perfil.id),
      fetchSubastasGanadas(perfil.id),
      fetchMisPujas(perfil.id),
      fetchMisReviews(perfil.id),
    ]);
    setLoadingData(false);
  };

  const fetchCompras = async (pid: string) => {
    const { data } = await supabase
      .from('orders')
      .select('*, products(*)')
      .eq('comprador_id', pid)
      .order('created_at', { ascending: false });
    if (data) setCompras(data);
  };

  const fetchSubastasGanadas = async (pid: string) => {
    const { data } = await supabase
      .from('products')
      .select('*')
      .eq('ganador_id', pid)
      .eq('tipo_venta', 'subasta')
      .order('updated_at', { ascending: false });
    if (data) setSubastasGanadas(data);
  };

  const fetchMisPujas = async (pid: string) => {
    const { data } = await supabase
      .from('bids')
      .select('*, products(*)')
      .eq('pujador_id', pid)
      .order('created_at', { ascending: false });
    if (data) setMisPujas(data);
  };

  const fetchMisReviews = async (pid: string) => {
    const { data } = await supabase
      .from('reviews')
      .select('*')
      .eq('comprador_id', pid);
    if (data) {
      const map: Record<string, any> = {};
      data.forEach(r => { map[r.order_id] = r; });
      setMisReviews(map);
    }
  };

  const handleReviewSubmitted = () => {
    if (profileId) fetchMisReviews(profileId);
    setReviewModal(null);
  };

  const getEstadoBadge = (estado: string) => {
    const map: Record<string, { label: string; cls: string; icon: string }> = {
      pagado: { label: 'Pagado', cls: 'bg-green-100 text-green-700', icon: 'ri-checkbox-circle-line' },
      pendiente: { label: 'Pendiente', cls: 'bg-yellow-100 text-yellow-700', icon: 'ri-time-line' },
      enviado: { label: 'En Camino', cls: 'bg-sky-100 text-sky-700', icon: 'ri-truck-line' },
      entregado: { label: 'Entregado', cls: 'bg-emerald-100 text-emerald-700', icon: 'ri-checkbox-circle-fill' },
      completado: { label: 'Completado', cls: 'bg-emerald-100 text-emerald-700', icon: 'ri-checkbox-circle-fill' },
      cancelado: { label: 'Cancelado', cls: 'bg-red-100 text-red-700', icon: 'ri-close-circle-line' },
      reservado: { label: 'Reservado', cls: 'bg-orange-100 text-orange-700', icon: 'ri-bookmark-line' },
    };
    return map[estado] || { label: estado, cls: 'bg-gray-100 text-gray-600', icon: 'ri-information-line' };
  };

  if (loading || loadingData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-24 pb-16 flex items-center justify-center min-h-[60vh]">
          <div className="w-12 h-12 border-4 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="bg-gray-900 rounded-2xl p-8 mb-8 text-white">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold mb-1">Mis Compras</h1>
                <p className="text-gray-400 text-sm">Historial de compras, subastas ganadas y pujas activas</p>
              </div>
              <div className="mt-4 md:mt-0 flex items-center gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold">{compras.length}</p>
                  <p className="text-xs text-gray-400">Compras</p>
                </div>
                <div className="w-px h-10 bg-white/20"></div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{subastasGanadas.length}</p>
                  <p className="text-xs text-gray-400">Subastas Ganadas</p>
                </div>
                <div className="w-px h-10 bg-white/20"></div>
                <div className="text-center">
                  <p className="text-2xl font-bold">{misPujas.length}</p>
                  <p className="text-xs text-gray-400">Pujas</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 mb-6 bg-white rounded-xl p-1 border border-gray-200 w-fit">
            {([
              { id: 'compras', label: 'Mis Compras', icon: 'ri-shopping-bag-line', count: compras.length },
              { id: 'subastas', label: 'Subastas Ganadas', icon: 'ri-trophy-line', count: subastasGanadas.length },
              { id: 'pujas', label: 'Historial de Pujas', icon: 'ri-auction-line', count: misPujas.length },
            ] as { id: TabType; label: string; icon: string; count: number }[]).map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === t.id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className={t.icon}></i>
                {t.label}
                <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === t.id ? 'bg-white/20' : 'bg-gray-100'
                }`}>{t.count}</span>
              </button>
            ))}
          </div>

          {/* Tab: Mis Compras */}
          {activeTab === 'compras' && (
            <div className="space-y-4">
              {compras.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <i className="ri-shopping-bag-3-line text-4xl text-gray-400"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Aún no tienes compras</h3>
                  <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                    Explora nuestro catálogo y encuentra productos increíbles en Puerto Rico.
                  </p>
                  <Link to="/catalogo" className="inline-flex items-center gap-2 px-7 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap">
                    <i className="ri-shopping-cart-line"></i>
                    Explorar Catálogo
                  </Link>
                </div>
              ) : (
                compras.map((compra) => {
                  const badge = getEstadoBadge(compra.estado);
                  const yaReseno = !!misReviews[compra.id];
                  const puedeResenar = (compra.estado === 'entregado' || compra.estado === 'completado') && !yaReseno;

                  return (
                    <div key={compra.id} className={`bg-white rounded-xl border p-5 transition-colors ${
                      compra.estado === 'cancelado' ? 'border-red-200 bg-red-50/20' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        {/* Imagen */}
                        <div className="w-full md:w-24 h-24 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0">
                          <img
                            src={compra.products?.fotos?.[0] || 'https://public.readdy.ai/ai/img_res/8d9b1206ae5f7238f9e168a01c34f836.jpg'}
                            alt={compra.products?.titulo}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${badge.cls}`}>
                              <i className={badge.icon}></i>
                              {badge.label}
                            </span>
                          </div>

                          <h3 className="font-bold text-gray-900 mb-0.5 text-sm">
                            {compra.products?.titulo || 'Artículo'}
                          </h3>
                          <p className="text-xs text-gray-400 mb-3">
                            Orden #{compra.id.slice(0, 8).toUpperCase()}
                          </p>

                          {/* Timeline visual */}
                          {compra.estado !== 'pendiente' && (
                            <OrderTimeline
                              estado={compra.estado}
                              createdAt={compra.created_at}
                              trackingNumber={compra.tracking_number}
                              uspsTrackingNumber={compra.usps_tracking_number}
                            />
                          )}

                          {/* Reseña */}
                          {puedeResenar && (
                            <button
                              onClick={() => setReviewModal({ order: compra, vendedorId: compra.vendedor_id, productoId: compra.producto_id })}
                              className="mt-3 flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold rounded-xl hover:bg-amber-100 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-star-line"></i>
                              Dejar Reseña al Vendedor
                            </button>
                          )}
                          {yaReseno && (
                            <div className="mt-3 flex items-center gap-2 text-xs text-emerald-600 font-semibold">
                              <i className="ri-checkbox-circle-fill"></i>
                              Reseña enviada
                              <div className="flex items-center gap-0.5">
                                {[1,2,3,4,5].map(s => (
                                  <i key={s} className={`${s <= misReviews[compra.id]?.estrellas ? 'ri-star-fill' : 'ri-star-line'} text-amber-400 text-xs`}></i>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Precio */}
                        <div className="text-right flex-shrink-0">
                          <p className="text-xl font-bold text-gray-900">${compra.monto_total?.toFixed(2)}</p>
                          <p className="text-xs text-gray-400 mt-0.5">Total pagado</p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Tab: Subastas Ganadas */}
          {activeTab === 'subastas' && (
            <div className="space-y-4">
              {subastasGanadas.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
                  <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <i className="ri-trophy-line text-4xl text-amber-500"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Aún no has ganado subastas</h3>
                  <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                    Participa en nuestras subastas y consigue productos a precios increíbles.
                  </p>
                  <Link to="/subastas" className="inline-flex items-center gap-2 px-7 py-3 bg-amber-500 text-white font-bold rounded-xl hover:bg-amber-600 transition-colors whitespace-nowrap">
                    <i className="ri-auction-line"></i>
                    Ver Subastas Activas
                  </Link>
                </div>
              ) : (
                subastasGanadas.map((subasta) => (
                  <div key={subasta.id} className="bg-white rounded-xl border border-amber-200 bg-amber-50/20 p-5 hover:border-amber-300 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      <div className="w-full md:w-24 h-24 bg-gray-100 rounded-xl overflow-hidden flex-shrink-0">
                        <img
                          src={subasta.fotos?.[0] || 'https://readdy.ai/api/search-image?query=auction%20winner%20trophy%20gold%20prize%20celebration%20professional&width=200&height=200&seq=subasta-won&orientation=squarish'}
                          alt={subasta.titulo}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="flex items-center gap-1 px-2.5 py-0.5 bg-amber-500 text-white rounded-full text-xs font-bold">
                            <i className="ri-trophy-fill"></i>
                            Ganador
                          </span>
                          {subasta.categoria === 'autos' && (
                            <span className="px-2.5 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">Auto</span>
                          )}
                          {subasta.categoria === 'bienes_raices' && (
                            <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">Propiedad</span>
                          )}
                        </div>
                        <h3 className="font-bold text-gray-900 mb-1 text-sm">{subasta.titulo}</h3>
                        <p className="text-xs text-gray-500">
                          Puja ganadora: <span className="font-bold text-amber-600 text-sm">${subasta.puja_actual?.toLocaleString()}</span>
                        </p>
                      </div>
                      <Link
                        to={`/producto/${subasta.id}`}
                        className="px-5 py-2.5 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap text-sm"
                      >
                        Ver Detalles
                      </Link>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Tab: Historial de Pujas */}
          {activeTab === 'pujas' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              {misPujas.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <i className="ri-auction-line text-4xl text-gray-400"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Aún no has pujado</h3>
                  <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
                    Explora las subastas activas y comienza a pujar.
                  </p>
                  <Link to="/subastas" className="inline-flex items-center gap-2 px-7 py-3 bg-amber-500 text-white font-bold rounded-xl hover:bg-amber-600 transition-colors whitespace-nowrap">
                    <i className="ri-auction-line"></i>
                    Ver Subastas
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {misPujas.map((puja) => {
                    const esGanadora = puja.products?.ganador_id === profileId && puja.monto === puja.products?.puja_actual;
                    const subastaActiva = puja.products?.estado_subasta === 'activa';
                    const esMiPujaActual = puja.monto === puja.products?.puja_actual;

                    return (
                      <div key={puja.id} className={`border rounded-xl p-4 transition-colors ${
                        esGanadora ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                              esGanadora ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600'
                            }`}>
                              <i className={esGanadora ? 'ri-trophy-fill' : 'ri-auction-line'}></i>
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                <h4 className="font-semibold text-gray-900 text-sm">{puja.products?.titulo}</h4>
                                {subastaActiva && esMiPujaActual && !esGanadora && (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px] font-bold">Puja Líder</span>
                                )}
                                {subastaActiva && !esMiPujaActual && (
                                  <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-[10px] font-bold">Superada</span>
                                )}
                                {!subastaActiva && esGanadora && (
                                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-bold">¡Ganaste!</span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">
                                {new Date(puja.created_at).toLocaleDateString('es-PR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className={`font-bold text-lg ${esGanadora ? 'text-amber-600' : 'text-gray-900'}`}>
                              ${puja.monto?.toLocaleString()}
                            </p>
                            {subastaActiva && (
                              <Link to={`/producto/${puja.producto_id}`} className="text-xs text-gray-500 hover:text-gray-700 underline whitespace-nowrap">
                                Ver subasta
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Reseña */}
      {reviewModal && (
        <ReviewModal
          order={reviewModal.order}
          vendedorId={reviewModal.vendedorId}
          productoId={reviewModal.productoId}
          compradorId={profileId}
          onClose={() => setReviewModal(null)}
          onSubmitted={handleReviewSubmitted}
        />
      )}

      <Footer />
    </div>
  );
};

export default MisComprasPage;
